import dayjs from "dayjs";
import * as _ from "lodash";

import { types } from "@/actions/map";

const generalInitialState = {
    highlight: {
        column: null,
        row: null,
    },
    lightTypes: [],
    selectedLights: [],
    selectedMarkers: [],
    deletedLights: [],
    recreatedLights: {},
    changedLights: [],
    arrayOfLights: {
        lights: 1,
        direction: "right",
        spacing: 0,
    },
    settings: {},
    zoom: 7,
    center: null,
    selectedTool: null,
    selectionEnabled: true,
    settingsModalVisible: false,
    mode: { name: "view", props: {} },
    syncingStatus: {
        lastChange: dayjs(),
        lastSync: dayjs(),
        name: "success",
        message: "",
        lastSavedIndex: 1,
    },
    syncingErrorMessage: null,
    filteredFlags: [],
    searchedLights: [],
    floorspaceCatalogs: [],
    floorSpaceCatalogMap: {},
    clipboard: [],
    pasteMode: {
        selectedLights: [],
        activated: false,
    },
    pendingGroupGeometry: null,
    editingGroupGeometry: null,
    hoveredGroupGeometry: null,
    lightGroups: [],
    defaultLightGroups: [],
    lightGroupSettings: {},
    editingLightGroupSettings: {},
    pendingLightGroupSettings: null,
    lightGroupSettingsStatus: null,
    lightGroupSettingsProgress: null,
    lightGroupSettingsPublishAllConfirming: false,
    uncatalogConfirming: false,
    site: null,
    floorspaceFileUrl: null,
    dimTest: {
        group: null,
        lightLevel: null,
        status: null,
        message: null,
    },
};

/**
 * Reducer for the general map state including lights, modes, tools, and group settings.
 *
 * @param state - The current general map state.
 * @param root0 - The dispatched action.
 * @param root0.payload - The action payload.
 * @param root0.type - The action type.
 * @returns The next general map state.
 */
const generalReducer = (state = generalInitialState, { payload, type }) => {
    switch (type) {
        case types.GENERAL.SET_HIGHLIGHT:
            return {
                ...state,
                highlight: _.defaultsDeep({}, payload, state.highlight),
            };
        case types.GENERAL.SET_SELECTED_TOOL:
            return {
                ...state,
                selectedTool: payload,
            };
        case types.GENERAL.SELECT_LIGHTS: {
            let selectedLights =
                payload.lightsIds.length > 0
                    ? _.xor(state.selectedLights, payload.lightsIds)
                    : payload.lightsIds;
            if (payload.selectionType === "single") {
                selectedLights = _.some(
                    state.selectedLights,
                    (selectedLight) => selectedLight === payload.lightsIds[0],
                )
                    ? []
                    : [payload.lightsIds[0]];
            }
            return {
                ...state,
                selectedLights,
                selectedMarkers: [],
            };
        }
        case types.GENERAL.DESELECT_LIGHTS: {
            return {
                ...state,
                selectedLights: _.difference(
                    state.selectedLights,
                    payload.lightsIds,
                ),
                selectedMarkers: [],
            };
        }
        case types.GENERAL.SET_CLIPBOARD:
            return {
                ...state,
                clipboard: payload,
            };
        case types.GENERAL.SET_PASTE_MODE:
            return {
                ...state,
                pasteMode: payload,
            };
        case types.GENERAL.CONFIGURE_ARRAY_OF_LIGHTS: {
            return {
                ...state,
                arrayOfLights: payload,
            };
        }
        case types.GENERAL.CLEAR_ARRAY_OF_LIGHTS:
            return {
                ...state,
                arrayOfLights: {
                    ...generalInitialState.arrayOfLights,
                },
            };
        case types.GENERAL.UPDATE_SETTINGS:
            return {
                ...state,
                settings: _.cloneDeep(payload),
                settingsModalVisible: !state.settingsModalVisible,
                syncingStatus: {
                    ...state.syncingStatus,
                    lastChange: dayjs(),
                    name:
                        state.syncingStatus.name !== "syncing"
                            ? "pending"
                            : state.syncingStatus.name,
                    message:
                        state.syncingStatus.name === "syncing"
                            ? state.syncingStatus.message
                            : "",
                },
            };
        case types.VALUES.GLOBAL.UPDATE_LIGHTS:
        case types.VALUES.GLOBAL.DELETE_LIGHTS:
        case types.VALUES.GLOBAL.SET_LIGHTS:
        case types.VALUES.GLOBAL.ADD_GROUP_GEOMETRY:
        case types.VALUES.GLOBAL.DELETE_GROUP_GEOMETRY:
        case types.VALUES.GLOBAL.SET_GROUP_GEOMETRIES:
        case types.VALUES.GLOBAL.UPDATE_MARKERS:
        case types.VALUES.GLOBAL.DELETE_MARKERS:
        case types.VALUES.GLOBAL.UNDO:
        case types.VALUES.GLOBAL.REDO:
            return {
                ...state,
                syncingStatus: {
                    ...state.syncingStatus,
                    lastChange: dayjs(),
                    name:
                        state.syncingStatus.name !== "syncing"
                            ? "pending"
                            : state.syncingStatus.name,
                    message:
                        state.syncingStatus.name === "syncing"
                            ? state.syncingStatus.message
                            : "",
                },
            };
        case types.GENERAL.SET_SYNCING_STATUS:
            return {
                ...state,
                syncingStatus: {
                    ...state.syncingStatus,
                    ...payload,
                },
            };
        case types.GENERAL.SET_SYNCING_ERROR_MESSAGE:
            return {
                ...state,
                syncingErrorMessage: payload,
            };
        case types.GENERAL.SET_FILTERED_FLAGS:
            return {
                ...state,
                selectedLights: [],
                filteredFlags: payload,
            };
        case types.GENERAL.SET_SEARCHED_LIGHTS:
            return {
                ...state,
                selectedLights: [],
                searchedLights: payload,
            };
        case types.GENERAL.SAVE_CHANGES:
            return {
                ...state,
                syncingStatus: {
                    ...state.syncingStatus,
                    lastSync: dayjs(),
                    name: "syncing",
                    message: "",
                },
            };
        case types.GENERAL.UPDATE_LIGHT_TYPES:
            return {
                ...state,
                lightTypes: payload,
            };
        case types.GENERAL.UPDATE_MAP:
            return {
                ...state,
                zoom: payload.zoom,
                center: _.values(payload.center),
            };
        case types.GENERAL.TOGGLE_SETTINGS_VISIBILITY:
            return {
                ...state,
                settingsModalVisible: !state.settingsModalVisible,
            };
        case types.GENERAL.DESELECT_ALL_LIGHTS: {
            return {
                ...state,
                selectedLights: [],
            };
        }
        case types.GENERAL.CHANGE_MODE:
            return {
                ...state,
                selectedTool: null,
                clipboard: [],
                pasteMode: generalInitialState.pasteMode,
                mode: payload,
                floorspaceCatalogs: [],
                selectedLights: [],
                selectedMarkers: [],
                filteredFlags: [],
                searchedLights: [],
                selectionEnable: true,
            };
        case types.GENERAL.SET_FLOORSPACE_CATALOGS:
            return {
                ...state,
                floorspaceCatalogs: payload,
            };
        case types.GENERAL.SET_FLOOR_SPACE_CATALOG_MAP:
            return {
                ...state,
                floorSpaceCatalogMap: payload,
            };
        case types.GENERAL.SET_LIGHT_SELECTION:
            return {
                ...state,
                selectedLights: [],
                selectionEnabled: payload,
            };
        case types.GENERAL.SET_SETTINGS:
            return {
                ...state,
                settings: _.cloneDeep(payload),
            };
        case types.GENERAL.SET_HOVERED_GROUP_GEOMETRY:
            return {
                ...state,
                hoveredGroupGeometry: payload,
            };
        case types.GENERAL.SET_PENDING_GROUP_GEOMETRY:
            return {
                ...state,
                pendingGroupGeometry: payload,
            };
        case types.GENERAL.SET_EDITING_GROUP_GEOMETRY:
            return {
                ...state,
                editingGroupGeometry: payload,
            };
        case types.GENERAL.SET_LIGHT_GROUPS:
            return {
                ...state,
                lightGroups: payload,
            };
        case types.GENERAL.ADD_LIGHT_GROUP:
            return {
                ...state,
                lightGroups: [...state.lightGroups, payload],
            };
        case types.GENERAL.SET_DEFAULT_LIGHT_GROUPS:
            return {
                ...state,
                defaultLightGroups: payload,
            };
        case types.GENERAL.SET_LIGHT_GROUP_SETTINGS_PROGRESS:
            return {
                ...state,
                lightGroupSettingsProgress: payload,
            };
        case types.GENERAL.SET_LIGHT_GROUP_SETTINGS_PUBLISH_ALL_CONFIRMING:
            return {
                ...state,
                lightGroupSettingsPublishAllConfirming: payload,
            };
        case types.GENERAL.SET_UNCATALOG_CONFIRMING:
            return {
                ...state,
                uncatalogConfirming: payload,
            };
        case types.GENERAL.SET_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                lightGroupSettings: {
                    ...state.lightGroupSettings,
                    ...payload,
                },
            };
        case types.GENERAL.PUT_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                lightGroupSettings: {
                    ...state.lightGroupSettings,
                    [payload.groupID]: payload,
                },
            };
        case types.GENERAL.SET_EDITING_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                editingLightGroupSettings: _.cloneDeep(payload),
            };
        case types.GENERAL.UPDATE_EDITING_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                editingLightGroupSettings: {
                    ...state.editingLightGroupSettings,
                    ...payload,
                },
            };
        case types.GENERAL.SAVE_AND_PUBLISH_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                pendingLightGroupSettings: _.cloneDeep(
                    state.editingLightGroupSettings,
                ),
                editingLightGroupSettings: {},
            };
        case types.GENERAL.SAVE_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                pendingLightGroupSettings: _.cloneDeep(
                    state.editingLightGroupSettings,
                ),
                editingLightGroupSettings: {},
            };
        case types.GENERAL.SET_PENDING_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                pendingLightGroupSettings: payload,
            };
        case types.GENERAL.CLEAR_PENDING_LIGHT_GROUP_SETTINGS:
            return {
                ...state,
                pendingLightGroupSettings: null,
            };
        case types.GENERAL.SET_LIGHT_GROUP_SETTINGS_STATUS:
            return {
                ...state,
                lightGroupSettingsStatus: payload,
            };
        case types.GENERAL.SET_SITE:
            return {
                ...state,
                site: payload,
            };
        case types.GENERAL.SET_FLOORSPACE_FILE_URL:
            return {
                ...state,
                floorspaceFileUrl: payload,
            };
        case types.GENERAL.START_DIM_TEST: {
            const { group, lightLevel } = payload;
            return {
                ...state,
                dimTest: {
                    ...state.dimTest,
                    group,
                    lightLevel,
                    status: "loading",
                    message: null,
                },
            };
        }
        case types.GENERAL.SET_DIM_TEST_STATUS: {
            const { status, message } = payload;
            return {
                ...state,
                dimTest: {
                    ...state.dimTest,
                    status,
                    message,
                },
            };
        }
        case types.GENERAL.SELECT_MARKER: {
            const selectedMarkers = state.selectedMarkers.includes(
                payload.markerId,
            )
                ? []
                : [payload.markerId];

            return {
                ...state,
                selectedLights: [],
                selectedMarkers,
            };
        }
        case types.GENERAL.SELECT_MARKERS:
            return {
                ...state,
                selectedMarkers: payload,
            };
        case types.GENERAL.DESELECT_ALL_MARKERS:
            return {
                ...state,
                selectedMarkers: [],
            };
        default:
            return state;
    }
};

export default generalReducer;
